import { UIElement } from '../ui-element';
import { debounce } from '../../global/helpers';
import { updateElement } from '../../global/ui-helpers';
import { isEscapePressed } from '../../global/keyboard';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { Debounce, NullOr } from '../../global/utils.type';
import styles from './ui-navsearch.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UINavSearch
 * @element ui-navsearch
 * @classdesc Represents a class for <code>ui-navsearch</code> element
 * @fires event:menu-search-toggle
 * @fires event:menu-search-instant
 * @fires event:menu-search-request
 * @property {HTMLButtonElement} toggler
 * @property {HTMLInputElement} input
 * @property {string | attr} label label for search input
 * @property {string | attr} labelOpen label for open button
 * @property {string | attr} labelClose label for close button
 * @property {HTMLFormElement} form
 * @example
 * <ui-navsearch></ui-navsearch>
 */
@Component({ tag: 'ui-navsearch', styles })
export class UINavSearch extends UIElement {
    @Attribute(Boolean) active?: boolean;
    @Attribute(String) labelOpen?: string;
    @Attribute(String) labelClose?: string;
    @Attribute(String) label?: string;

    @Child('.ui-navsearch__form') form!: HTMLFormElement;
    @Child('.ui-navsearch__input') input!: HTMLInputElement;
    @Child('button[type="button"]') toggler!: HTMLButtonElement;

    private readonly toggleSearchHandler = this.handleToggleSearch.bind(this);
    private readonly inputFocusHandler = this.handleInputFocus.bind(this);
    private readonly inputUpdateHandler: Debounce = debounce(
        this.handleInputUpdate.bind(this),
        400
    );
    private readonly submitSearchHandler = this.handleSubmitSearch.bind(this);
    private readonly escapeHandler = this.handleEscape.bind(this);

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['active'];
    }

    /**
     * @type {string}
     */
    getValue(): string {
        return this.input.value.trim();
    }

    setValue(value: string): string {
        return (this.input.value = value.trim());
    }

    /**
     * Opens the search bar
     * @fires event:menu-search-toggle
     */
    activate(): void {
        this.active = true;
        this.dispatchCustomEvent('menu-search-toggle', { active: this.active });
        this.input.focus();
    }

    /**
     * Closes the search bar
     * @fires event:menu-search-toggle
     */
    deactivate(): void {
        this.active = false;
        this.dispatchCustomEvent('menu-search-toggle', { active: this.active });
    }

    /**
     * Fires callback when item state is updated
     * @private
     */
    private onChangeActiveState(): void {
        const icon = this.toggler.querySelector('ui-icon');
        icon?.setAttribute(
            'glyph',
            this.active ? 'redesign-x-mark' : 'redesign-magnifying-glass'
        );
        updateElement(this.toggler, {
            classList: {
                active: !!this.active,
            },
            attributes: {
                'aria-label':
                    (this.active ? this.labelClose : this.labelOpen) ||
                    this.label ||
                    null,
            },
        });
    }

    /**
     * @param {Event} e
     */
    handleInputFocus(e: Event): void {
        (e.target as HTMLInputElement).value = '';
    }

    /**
     * @param {Event} e
     * @fires event:menu-search-instant
     */
    handleInputUpdate(e: Event): void {
        this.dispatchCustomEvent('menu-search-instant', {
            query: (e.target as HTMLInputElement).value,
        });
    }

    /**
     * @param {Event} e
     */
    handleToggleSearch(e: Event): void {
        if (!this.active) {
            this.activate();
        } else {
            this.deactivate();
        }
    }

    /**
     * @param {Event} e
     * @fires event:menu-search-request
     */
    handleSubmitSearch(e: Event): void {
        e.preventDefault();
        this.dispatchCustomEvent('menu-search-request', {
            query: this.input.value,
        });
    }

    /**
     * @param {KeyboardEvent} event
     */
    handleEscape(event: KeyboardEvent): void {
        if (isEscapePressed(event) && this.active) {
            this.deactivate();
            this.toggler.focus();
        }
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        switch (name) {
            case 'active':
                this.onChangeActiveState();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /** @private */
    private subscribeEvents(): void {
        this.toggler.addEventListener('click', this.toggleSearchHandler);
        this.input.addEventListener('focus', this.inputFocusHandler);
        this.input.addEventListener('input', this.inputUpdateHandler);
        this.form.addEventListener('submit', this.submitSearchHandler);
        this.addEventListener('keydown', this.escapeHandler);
    }

    /** @private */
    private unsubscribeEvents(): void {
        this.toggler?.removeEventListener('click', this.toggleSearchHandler);
        this.input?.removeEventListener('focus', this.inputFocusHandler);
        this.input?.removeEventListener('input', this.inputUpdateHandler);
        this.inputUpdateHandler.cancel();
        this.form?.removeEventListener('submit', this.submitSearchHandler);
        this.removeEventListener('keydown', this.escapeHandler);
    }

    /**
     * Renders item as a primary search
     */
    override render(): void {
        this.updateElement({
            attributes: {
                role: 'search',
            },
            classList: {
                '-primary': true,
                '-search': true,
            },
            children: [
                {
                    tagName: 'form',
                    attributes: {
                        class: 'ui-navsearch__form',
                    },
                    children: [
                        {
                            tagName: 'input',
                            attributes: {
                                'aria-label': this.label,
                                'autocomplete': 'off',
                                'name': 'search-query',
                                'type': 'text',
                                'role': 'searchbox',
                                'placeholder': this.label,
                                'class': 'ui-navsearch__input',
                            },
                        },
                        {
                            tagName: 'button',
                            attributes: {
                                'name': 'searchSubmit',
                                'type': 'submit',
                                'aria-label': this.label,
                                'value': '',
                                'class': 'ui-navsearch__submit',
                            },
                            children: [
                                {
                                    tagName: 'ui-icon',
                                    attributes: {
                                        glyph: 'redesign-magnifying-glass',
                                    },
                                },
                            ],
                        },
                    ],
                },
                {
                    tagName: 'button',
                    attributes: {
                        'aria-label': this.labelOpen || this.label || null,
                        'value': this.label, // TODO: check if it is really needed for ibank
                        'class': 'ui-navitem__icon-button',
                        'type': 'button',
                    },
                    children: [
                        {
                            tagName: 'ui-icon',
                            attributes: {
                                glyph: this.active
                                    ? 'redesign-x-mark'
                                    : 'redesign-magnifying-glass',
                            },
                        },
                    ],
                },
            ],
        });
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
